import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts'
import type { Device } from '@/types/device.types'

interface TankLevelChartProps {
  devices: Device[]
}

interface ChartDataPoint {
  name: string
  level: number
  serialNumber: string
  deviceId: string
  fill: string
}

// Helper function to get liquid level percentage from measurement
const getLiquidLevelPercentage = (device: Device): number => {
  if (!device.lastMeasurement || typeof device.lastMeasurement !== 'object') {
    return -1
  }

  const measurement = device.lastMeasurement as any

  // For watersense devices
  if (device.deviceType === 'watersense') {
    if (measurement.liquid_level_percentage !== undefined) {
      return measurement.liquid_level_percentage
    }
    if (measurement.liquid_level !== undefined && measurement.liquid_level <= 2047) {
      // Convert liquid level to percentage (2047 is max)
      return (measurement.liquid_level / 2047) * 100
    }
  }

  return -1
}

// Color based on level (copied from ColdFusion implementation)
const getBarColor = (level: number): string => {
  if (level < 0) return '#808080' // grey for no data
  if (level < 25) return '#ff0000' // red
  if (level < 50) return '#ffa500' // orange
  if (level < 75) return '#ffdd22' // yellow
  return '#008000' // green
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null

  const data = payload[0].payload as ChartDataPoint

  return (
    <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
      <p className="font-bold">{data.name}</p>
      <p className="text-sm">Level: {Math.round(data.level)}%</p>
      <p className="text-sm">Serial: {data.serialNumber}</p>
    </div>
  )
}

export const TankLevelChart = ({ devices }: TankLevelChartProps) => {
  const navigate = useNavigate()

  // Filter devices with liquid level data and sort by level (lowest first)
  const devicesWithLevels = devices
    .map((device) => ({
      device,
      level: getLiquidLevelPercentage(device)
    }))
    .filter((item) => item.level >= 0)
    .sort((a, b) => a.level - b.level)
    .slice(0, 10) // Take lowest 10

  if (devicesWithLevels.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            No favorite devices found.
          </p>
          <p className="text-sm text-muted-foreground">
            Add a device to your favorites to see it here.
          </p>
        </div>
      </div>
    )
  }

  const chartData: ChartDataPoint[] = devicesWithLevels.map(({ device, level }) => ({
    name: device.name,
    level: Math.round(level),
    serialNumber: device.serialNumber,
    deviceId: device.id,
    fill: getBarColor(level)
  }))

  const handleBarClick = (data: ChartDataPoint) => {
    navigate(`/devices/${data.deviceId}`)
  }

  return (
    <div className="w-full h-[350px]">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Lowest 10 Levels</h3>
        <p className="text-sm text-muted-foreground">
          For devices in your linked accounts + favorites
        </p>
      </div>

      <ResponsiveContainer width="100%" height="85%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 12 }}
            style={{ cursor: 'pointer' }}
          />
          <YAxis
            label={{ value: 'Level (% Full)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="level"
            onClick={handleBarClick}
            cursor="pointer"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="level"
              position="top"
              formatter={(value: number) => `${value}%`}
              style={{ fontSize: 12, fontWeight: 'bold' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
